import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Box,
  Button,
  Icon,
  Link,
  SelectOption,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { AvailableModules } from 'generated/featureDefinitions'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import { GetModuleAvailabilitiesDocument } from 'generated/resources/graphql'
import { PayrollExportModalDocument } from 'generated/resources/graphql'
import param from 'jquery-param'
import moment from 'moment'

import Callout from 'modules/core/components/Callout'
import DayPicker from 'modules/core/components/DayPicker'
import { LeaveTypeSelectorInput } from 'modules/core/components/Inputs/LeaveTypeSelectorInput'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import companies from 'modules/core/store/collections/companies'
import legalEntities from 'modules/core/store/collections/legalEntities'

import { useCompanyIncidenceSettings } from 'modules/bookkeepers/hooks/useCompanyIncidenceSettings'
import { PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { FACTORIAL_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { downloadIncidencesExport } from 'modules/bookkeepers/lib/useDownloadIncidencesExport'
import { multiTrack } from 'modules/compensations/lib/tracking'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { DATA_VIEW, TDataViewValues } from 'modules/payroll_bundle/lib/Incidences/Status'
import { EMPLOYEE_CHANGE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { statusOptions } from 'modules/payroll_bundle/pages/IncidencesPage/components/_FiltersBar/_IncidencesFilters/filterOptions'
import policies from 'modules/payroll_bundle/store/collections/policies'
import type Policy from 'modules/payroll_bundle/store/models/Policy'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import { useExportableOptionsByModules } from 'modules/payroll_exporter/hooks/useExportableOptionsByModules'
import { downloadFile } from 'modules/payroll_exporter/lib/downloadFile'
import {
  DELTA_FORMATS,
  EVENT_TYPES,
  ExportFormat,
  NEED_ABSENCES_DATA,
  NEED_DATE_FORMATS,
  NEED_EVENTS_STATUS,
  NEED_UPDATE_FORMATS,
  NO_MAX_DATE_DIFF_FORMATS,
  eventsToExportTypeMap,
  isPayrollExportFormat,
} from 'modules/payroll_exporter/lib/format'
import { getDescriptionForFormat } from 'modules/payroll_exporter/lib/formatDescriptions'
import legalEntitiesSettings from 'modules/payroll_exporter/store/collections/legalEntitiesSettings'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

import CalculationStatusNotice from './CalculationStatusNotice'
import WarningBanner from './WarningBanner'

interface ExportParams {
  type: string
  from: string
  to: string
  legal_entity: string | null
  update_export_period_type: string
  payroll_policy_period_id: number | null
  company_id: number | null
  bookkeeper_view: boolean
  update_from?: string
  update_to?: string
  employee_ids?: number[]
  leave_type_ids?: number[]
  show_absences_total?: string | null
  trim_leave_duration?: string | null
  timestamp: number
}

const PERIOD_ALL = 'all'
const PERIOD_CUSTOM = 'custom'
const MAX_DATE_DIFF = 31
const ZERO_DATE_DIFF = 0

// Feature flags
const devDeltaExportIncludeLastOneYearRecordFeatureId: AllFeatureIds =
  'dev_delta_export_include_last_one_year_record'
const inhousePayrollFeatureId: AllFeatureIds = 'inhouse_payroll'
const factorialProfessionalsFeatureId: AllFeatureIds = 'factorial_professionals'

const getTypeLabel = (type: ExportFormat) => {
  switch (type) {
    case ExportFormat.EVENTS_NEW_HIRES:
      return i18n.t('payroll_bundle.export.updates.new_hires')
    case ExportFormat.EVENTS_PERSONAL_CHANGES:
      return i18n.t('payroll_bundle.export.updates.personal_changes')
    case ExportFormat.EVENTS_TERMINATIONS:
      return i18n.t('payroll_bundle.export.updates.terminations')
  }
}

const mapEmployeeEventsToSettings = (type: string) => {
  switch (type) {
    case ExportFormat.EVENTS_NEW_HIRES:
      return ['hiring']
    case ExportFormat.EVENTS_PERSONAL_CHANGES:
      return Object.keys(EMPLOYEE_CHANGE_TYPES)
    case ExportFormat.EVENTS_TERMINATIONS:
      return ['termination']
    default:
      return null
  }
}

const isEmployeeEventsActive = (type: string, activeIncidencesList: string[]) => {
  const activeIncidenceTypes = new Set(activeIncidencesList)
  const typeSettingsNames = mapEmployeeEventsToSettings(type)

  return typeSettingsNames
    ? typeSettingsNames.some((item) => activeIncidenceTypes.has(item))
    : false
}

interface Props {
  onClose: () => void
  selectedCompany?: number | null
  isProfessionals?: boolean
}

const PayrollExportModal = ({
  selectedCompany = null,
  isProfessionals = false,
  onClose,
}: Props) => {
  const {
    policyPeriodCollection,
    currentPolicyPeriods,
    periodIndex,
    policyId: contextPolicyId,
    setCurrentPolicy,
    loadingPolicyPeriodCollection,
    prevPeriod,
    setPeriodId,
  } = usePolicyPeriods()

  const navigate = useNavigate()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [disabled, setDisabled] = useState<boolean>(false)

  const [updatesToBeIncluded, setUpdatesToBeIncluded] = useState<string>(PERIOD_ALL)
  const [showAbsencesTotal, setShowAbsencesTotal] = useState<string | null>(null)
  const [legalEntity, setLegalEntity] = useState<string | null>(null)

  const [eventStatus, setEventStatus] = useState<string | null>(null)

  const [preSelectedLeaveTypeIds, setPreSelectedLeaveTypeIds] = useLocalStorage<number[]>(
    LOCAL_STORAGE_KEYS.preSelectedLeaveTypeIds
  )
  const [leaveTypeIds, setSelectedLeaveTypes] = useState<number[]>(preSelectedLeaveTypeIds || [])
  const [trimLeaveDuration, setTrimLeaveDuration] = useState<string | null>(null)

  const { policyId: paramsPolicyId } = useParams()
  let currentPolicy: Policy | undefined
  const policyId = paramsPolicyId ? Number(paramsPolicyId) : contextPolicyId

  // HACK: Prevent the initial policy id from being changed, as that determines
  // if we should show the policy and policy period selectors or not
  const [policyIdSnapshot, setPolicyIdSnapshot] = useState<number | null>()

  useUnsafeEffect(() => {
    if (!policyIdSnapshot) {
      setPolicyIdSnapshot(policyId)
    }
  }, [loadingPolicyPeriodCollection]) // eslint-disable-line react-hooks/exhaustive-deps

  if (policyId !== null) {
    currentPolicy = policies.get(policyId)
  }
  const companyId = selectedCompany || currentPolicy?.attributes.company_id || currentCompanyId

  const currentPolicyPeriod: PolicyPeriod | undefined = currentPolicyPeriods?.at(periodIndex)
  const payrollCalculationStartedAt: string | null =
    prevPeriod?.attributes.calculation_started_at ?? null

  // For the auth system to understand we're in the bookkeeper context we need to pass down this
  // for GraphQL
  const bookkeeperContext = {
    isBookkeeperView: isProfessionals,
  }
  // for REST
  const bookkeeperData = {
    data: {
      bookkeeper_view: isProfessionals,
    },
  }

  const { loading: loadingFeatures, data: featuresData } = useQuery(PayrollExportModalDocument, {
    variables: {
      companyId: companyId,
    },
    context: bookkeeperContext,
  })

  const moduleAvailabilities = featuresData?.companies?.moduleAvailabilitiesConnection?.nodes || []

  const hasCompensationsModule = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.compensations
  )

  const hasDevDeltaExportIncludeLastOneYearRecord =
    featuresData?.features?.featuresConnection.nodes.some(
      (feature) =>
        feature.name === devDeltaExportIncludeLastOneYearRecordFeatureId && feature.enabled
    ) ?? false

  const hasInhousePayroll =
    featuresData?.features?.featuresConnection.nodes.some(
      (feature) => feature.name === inhousePayrollFeatureId && feature.enabled
    ) ?? false

  const hasProfessionals =
    featuresData?.features?.featuresConnection.nodes.some(
      (feature) => feature.name === factorialProfessionalsFeatureId && feature.enabled
    ) ?? false

  const [type, setType] = useState<ExportFormat>(
    hasCompensationsModule ? ExportFormat.EVENTS_NEW_HIRES : ExportFormat.SUPPLEMENTS
  )
  const canExportLastYearData = DELTA_FORMATS.has(type) && hasDevDeltaExportIncludeLastOneYearRecord

  const [fromDate, setFromDate] = useState<moment.Moment>(
    currentPolicyPeriod ? currentPolicyPeriod.attributes.starts_on : moment().startOf('month')
  )

  const [toDate, setToDate] = useState<moment.Moment>(
    currentPolicyPeriod ? currentPolicyPeriod.attributes.ends_on : moment().endOf('month')
  )

  const { navigation } = useNavigation()

  const { loading: loadingCollections } = useAsyncData(() => {
    const loadingPromise = Promise.all([
      policies.fetch(bookkeeperData),
      legalEntities.fetch(bookkeeperData).then(() =>
        legalEntitiesSettings.fetch({
          data: {
            bookkeeper_view: isProfessionals,
            ids: [legalEntities.find((element) => element.attributes.company_id === companyId)?.id],
          },
        })
      ),
      leaveTypes.fetch(bookkeeperData),
    ])
    return loadingPromise
  }, [isProfessionals])

  const isPolicyPeriodCollectionEmpty = policyPeriodCollection
    ? policyPeriodCollection.length === 0
    : true

  const setDefaultPolicyId = () => {
    if (
      !loadingCollections &&
      !loadingPolicyPeriodCollection &&
      !isPolicyPeriodCollectionEmpty &&
      contextPolicyId !== policyId
    ) {
      const companyPolicies = policies.filter(
        (policy) => policy.attributes.company_id === companyId
      )

      const policy = policyId
        ? companyPolicies.find((element) => element.attributes.id === policyId)
        : companyPolicies.find((element) => element.attributes.default) || companyPolicies.at(0)
      if (policy && policy.attributes.id) {
        setCurrentPolicy(policy.attributes.id)
      }
    }
  }

  useUnsafeEffect(setDefaultPolicyId, [
    companyId,
    isPolicyPeriodCollectionEmpty,
    loadingCollections,
    loadingPolicyPeriodCollection,
    contextPolicyId,
    setCurrentPolicy,
    policyId,
  ])

  const visibleLeaveTypes = leaveTypes.models.filter((element) => element.attributes.active)

  const onSelectLeaveTypes = (ids: number[]) => {
    setSelectedLeaveTypes(ids)
    setPreSelectedLeaveTypeIds(ids)
  }

  const [updateFromDate, setUpdateFromDate] = useState<moment.Moment>(moment().startOf('month'))
  const [updateToDate, setUpdateToDate] = useState<moment.Moment>(moment().endOf('month'))

  const legalEntitySettings = legalEntitiesSettings.at(0)
  const allowDateSelectors = hasCompensationsModule

  useEffect(() => {
    if (currentPolicyPeriod && !NEED_DATE_FORMATS.has(type)) {
      const startsOn = stringToDate(currentPolicyPeriod.attributes.starts_on, 'backend')
      const endsOn = stringToDate(currentPolicyPeriod.attributes.ends_on, 'backend')

      // toDate and fromDate may not be used in the backend,
      // setting the values here to ensure we use period dates to evaluate isValidRange
      setToDate(endsOn)
      setFromDate(startsOn)
      setUpdateFromDate(startsOn)
      setUpdateToDate(endsOn)
    }
  }, [periodIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  const onDownload = async () => {
    setDisabled(true)

    // reducing the period that can be exported to 3 months to avoid issues in downloading too much data
    const pastThreeMonthsStartDate = currentPolicyPeriod
      ? dateToString(
          stringToDate(currentPolicyPeriod?.attributes.starts_on, 'backend')
            .subtract(3, 'months')
            .startOf('month'),
          'backend'
        )
      : ''

    const pastThreeMonthsEndDate = currentPolicyPeriod
      ? dateToString(
          stringToDate(currentPolicyPeriod?.attributes.starts_on, 'backend').subtract(1, 'days'),
          'backend'
        )
      : ''

    const from =
      canExportLastYearData && !!pastThreeMonthsStartDate
        ? pastThreeMonthsStartDate
        : dateToString(fromDate, 'backend')

    const to =
      canExportLastYearData && !!pastThreeMonthsEndDate
        ? pastThreeMonthsEndDate
        : dateToString(toDate, 'backend')

    let data: ExportParams = {
      type,
      from,
      to,
      legal_entity: legalEntity,
      update_export_period_type: updatesToBeIncluded,
      payroll_policy_period_id: null,
      company_id: companyId || null,
      bookkeeper_view: isProfessionals,
      leave_type_ids: leaveTypeIds,
      show_absences_total: showAbsencesTotal,
      trim_leave_duration: trimLeaveDuration,
      /* Used to identify the error export with its export
      for different requests with the same params */
      timestamp: Date.now(),
    }

    if (EVENT_TYPES.has(type)) {
      const eventExportType = eventsToExportTypeMap(type) ?? ''

      const eventsFilters = {
        filterDate: {
          firstDate: dateToString(fromDate, 'backend'),
          lastDate: dateToString(toDate, 'backend'),
        },
        filters: {
          ...(legalEntity && { legal_entity_id: legalEntity }),
          ...(eventStatus && { status: eventStatus }),
        },
      }

      const dataView: TDataViewValues = hasInhousePayroll
        ? DATA_VIEW.payroll_in_house
        : DATA_VIEW.incidence_manager

      await downloadIncidencesExport(eventsFilters, dataView, eventExportType)

      setDisabled(false)
      onClose()

      if (hasCompensationsModule) {
        track(trackingEvents.compensations.exportEmployeeUpdates, {
          export_format: type,
          application: isProfessionals ? PROFESSIONALS_APP : FACTORIAL_APP,
        })
      }

      return
    }

    if (currentPolicyPeriod) {
      data.payroll_policy_period_id = currentPolicyPeriod.attributes.id
    }

    if (updateFromDate && updateToDate) {
      data = {
        ...data,
        update_from: dateToString(updateFromDate, 'backend'),
        update_to: dateToString(updateToDate, 'backend'),
      }
    }

    await downloadFile(param({ ...data, v: 2 }), data)

    const company = isProfessionals ? companies.find({ id: selectedCompany }) : companies.current

    const trackingData = {
      export_format: type,
      updates: updatesToBeIncluded,
      company_id: company?.attributes.id,
      company_name: company?.attributes.name,
      period: currentPolicyPeriod?.attributes.name,
      period_id: currentPolicyPeriod?.attributes.id,
      application: isProfessionals ? PROFESSIONALS_APP : FACTORIAL_APP,
    }

    if (hasCompensationsModule) {
      track(trackingEvents.compensations.exportEmployeeUpdates, {
        application: isProfessionals ? PROFESSIONALS_APP : FACTORIAL_APP,
        export_format: type,
      })
    }

    multiTrack(
      [trackingEvents.bookkeeper.clients.exportFile, trackingEvents.compensations.exportFile],
      trackingData
    )

    setDisabled(false)
    onClose()
  }

  const isValidRange = (): boolean => {
    const dateDiff = toDate.diff(fromDate, 'days')

    if (NO_MAX_DATE_DIFF_FORMATS.has(type)) return dateDiff >= 0

    return dateDiff >= 0 && dateDiff <= maxDateDiff
  }

  const { data } = useQuery(GetModuleAvailabilitiesDocument, {
    variables: {
      companyId: isProfessionals && selectedCompany ? selectedCompany : currentCompanyId,
      ids: ['timeoff', 'time_tracking', 'compensations'],
    },
  })

  const typeOptions = useExportableOptionsByModules({
    legalEntitySettings,
    groupExports: true,
    sortExports: true,
    moduleAvailabilities: data?.companies?.moduleAvailabilities ?? [],
  })

  const [openTrimToolTip, toggleTrimToolTip] = useToggle(false)

  const updatesToBeIncludedOptions: SelectOption[] = [
    {
      label: i18n.t('payroll_bundle.export.period_selector.all'),
      value: PERIOD_ALL,
    },
    {
      label: i18n.t('payroll_bundle.export.period_selector.custom_period'),
      value: PERIOD_CUSTOM,
    },
  ]

  const absencesData: SelectOption[] = [
    {
      label: i18n.t('payroll_bundle.export.absence_total'),
      value: true,
    },
    {
      label: i18n.t('payroll_bundle.export.absence_detail'),
      value: false,
    },
  ]

  const trimDateOptions = [
    {
      label: i18n.t('employees.reports.trim_leave_duration_off'),
      value: false,
    },
    {
      label: i18n.t('employees.reports.trim_leave_duration_on'),
      value: true,
    },
  ]

  const availablePeriodsForCurrentPolicy: SelectOption[] =
    currentPolicyPeriods?.map((policyPeriod) => {
      let subtractedName = policyPeriod.attributes.name

      if (currentPolicy?.attributes.name) {
        subtractedName = subtractedName.replace(`${currentPolicy.attributes.name} `, '')
      }

      return {
        label: subtractedName,
        value: policyPeriod.attributes.id,
      }
    }) ?? []

  const legalEntityList: SelectOption[] = legalEntities
    .filter((element) => element.attributes.company_id === companyId)
    .map((element) => {
      return {
        label: element.attributes.legal_name,
        value: element.attributes.id,
      }
    })

  const policyList: SelectOption[] = policies
    .filter((element) => element.attributes.company_id === companyId)
    .map((element) => {
      return {
        label: element.attributes.name,
        value: element.attributes.id,
      }
    })

  const maxDateDiff = useMemo(
    () => {
      if (allowDateSelectors) return MAX_DATE_DIFF

      if (!currentPolicyPeriod || !availablePeriodsForCurrentPolicy.length) return ZERO_DATE_DIFF

      const startsOn = stringToDate(currentPolicyPeriod.attributes.starts_on, 'backend')
      const endsOn = stringToDate(currentPolicyPeriod.attributes.ends_on, 'backend')

      return endsOn.diff(startsOn, 'days')
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodIndex]
  )

  const { loading: loadingPolicies, canManagePayrollIncidences } = usePayrollContextlessPolicies()

  const canSeePayrollBundleIncidences =
    (hasProfessionals || hasInhousePayroll) && canManagePayrollIncidences

  const isDisabled = () => {
    /*
      1. If type is employee event and can't see incidences, disable export button
      2. If type is employee event and can see incidences but the type is not enabled, disable export button
    */
    if (
      EVENT_TYPES.has(type) &&
      !hasProfessionals &&
      (!canSeePayrollBundleIncidences || !isEmployeeEventActivated)
    ) {
      return true
    }
    return disabled || !isValidRange() || (NEED_ABSENCES_DATA.has(type) && leaveTypeIds.length == 0)
  }

  const parseFilterChange = (value: string | null) => {
    return !value ? '' : value
  }

  const { companyIncidenceSettings } = useCompanyIncidenceSettings(companyId, isProfessionals)

  // filter by tracked incidences, which means they are activated
  const companyIndicenceActiveSettings = companyIncidenceSettings
    .filter((incidence) => incidence.trackIncidence)
    .map((incidence) => incidence.incidenceName)

  const isEmployeeEventActivated = isEmployeeEventsActive(type, companyIndicenceActiveSettings)

  const description = getDescriptionForFormat(type)

  const navigateToSilaeSettings = () => {
    const path = hasCompensationsModule
      ? navigation.settings.compensations.integrationSilae.fullPath()
      : navigation.settings.payroll_bundle.integrationSilae.fullPath()
    navigate(path)
  }

  useEffect(() => {
    if (canExportLastYearData) {
      setUpdateToDate(moment())

      payrollCalculationStartedAt &&
        setUpdateFromDate(stringToDate(payrollCalculationStartedAt, 'backend_datetime'))
    }
  }, [type, canExportLastYearData, payrollCalculationStartedAt])

  useUnsafeEffect(() => {
    if (policyId && !currentPolicy) {
      setCurrentPolicy(policyId)
    }
  }, [policyId, currentPolicy, setCurrentPolicy])

  const modal = () => {
    return (
      <Modal
        title={i18n.t('payroll_bundle.export.export_heading')}
        description={
          hasCompensationsModule ? undefined : i18n.t('payroll_bundle.export.export_description')
        }
        illustration="table-file"
        onClose={onClose}
        footer={
          <>
            <Button onClick={onClose} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            <Button onClick={onDownload} type="primary" disabled={isDisabled()}>
              {i18n.t('payroll_bundle.export.export_confirm')}
            </Button>
          </>
        }
      >
        <Box gap="s16">
          {legalEntityList.length > 1 && (
            <SelectField
              label={i18n.t('payroll_bundle.export.legal_entity')}
              value={legalEntity ?? ''}
              options={legalEntityList}
              none={i18n.t('payroll_bundle.export.all_legal_entities')}
              showOriginalLabelText
              onChange={(value: string | null) => setLegalEntity(value)}
              loading={loadingPolicyPeriodCollection}
            />
          )}

          {!hasCompensationsModule && isProfessionals && currentPolicy && policyIdSnapshot && (
            <>
              <TextField
                readonly
                label={i18n.t('contract_versions.forms.payroll_policy.title')}
                value={currentPolicy?.attributes.name}
              />

              <TextField
                readonly
                label={i18n.t('settings.work_schedule.period_section.period')}
                value={currentPolicyPeriod?.attributes.name}
              />
            </>
          )}

          {!hasCompensationsModule && isProfessionals && !policyIdSnapshot && (
            <>
              <SelectField
                label={i18n.t('contract_versions.forms.payroll_policy.title')}
                value={`${policyId}`}
                options={policyList}
                showOriginalLabelText
                none={i18n.t('common.select')}
                onChange={(value: string | null) => {
                  setCurrentPolicy(Number(value))
                }}
              />

              <SelectField
                label={i18n.t('settings.work_schedule.period_section.period')}
                value={`${currentPolicyPeriod?.attributes.id}`}
                options={availablePeriodsForCurrentPolicy}
                showOriginalLabelText
                none={i18n.t('common.select')}
                onChange={(value: string | null) => setPeriodId(Number(value))}
              />
            </>
          )}

          <SelectField
            label={i18n.t('payroll_bundle.export.export_data_format')}
            value={type}
            options={typeOptions}
            showOriginalLabelText
            onChange={(value: string) => {
              if (isPayrollExportFormat(value)) {
                setType(value)
                setUpdatesToBeIncluded(PERIOD_ALL)
              }
            }}
          />

          {NEED_EVENTS_STATUS.has(type) && (
            <Select
              label={i18n.t('payroll_bundle.export.event_status')}
              options={statusOptions(isProfessionals, hasInhousePayroll)}
              value={parseFilterChange(eventStatus)}
              onChange={(value: string) => {
                setEventStatus(value)
              }}
            />
          )}

          {NEED_ABSENCES_DATA.has(type) && (
            <LeaveTypeSelectorInput
              label={i18n.t('payroll_bundle.export.absence_type')}
              attachment="middle center"
              leaveTypes={visibleLeaveTypes}
              selectedIds={leaveTypeIds}
              onSubmit={onSelectLeaveTypes}
              showCreateLeave={false}
            />
          )}

          {(NEED_DATE_FORMATS.has(type) || allowDateSelectors) && (
            <Box flexDirection="row" gap="s16">
              <DayPicker
                label={i18n.t('payroll_bundle.export.start_date')}
                value={dateToString(fromDate, 'ui')}
                maxDate={toDate}
                onChange={(newDate: string) => setFromDate(stringToDate(newDate, 'ui'))}
              />
              <DayPicker
                label={i18n.t('payroll_bundle.export.end_date')}
                value={dateToString(toDate, 'ui')}
                minDate={fromDate}
                onChange={(newDate: string) => setToDate(stringToDate(newDate, 'ui'))}
              />
            </Box>
          )}

          {NEED_ABSENCES_DATA.has(type) && (
            <>
              <SelectField
                label={i18n.t('employees.reports.trim_leave_duration_label')}
                value={trimLeaveDuration ?? ''}
                secondaryLabel={
                  <Tooltip.Alt
                    attachment="bottom center"
                    open={openTrimToolTip}
                    toggle={toggleTrimToolTip}
                    label={i18n.t('employees.reports.trim_leave_duration_help')}
                  >
                    <Icon.Small icon="question" encircled="grey800" />
                  </Tooltip.Alt>
                }
                options={trimDateOptions}
                onChange={(value: string) => setTrimLeaveDuration(value)}
              />

              <SelectField
                label={i18n.t('payroll_bundle.export.absences_data')}
                value={showAbsencesTotal ?? ''}
                none={i18n.t('payroll_bundle.export.show_total_absences')}
                options={absencesData}
                showOriginalLabelText
                onChange={(value: string) => setShowAbsencesTotal(value)}
              />
            </>
          )}

          {(NEED_UPDATE_FORMATS.has(type) ||
            (DELTA_FORMATS.has(type) && !canExportLastYearData)) && (
            <SelectField
              label={i18n.t('payroll_bundle.export.period_selector.label')}
              value={updatesToBeIncluded}
              options={updatesToBeIncludedOptions}
              showOriginalLabelText
              onChange={(period: string) => setUpdatesToBeIncluded(period)}
            />
          )}

          {(updatesToBeIncluded === PERIOD_CUSTOM || canExportLastYearData) && (
            <Box flexDirection="row" gap="s16">
              <DayPicker
                label={i18n.t('payroll_bundle.export.period_date_range.from_label')}
                value={dateToString(updateFromDate, 'ui')}
                maxDate={updateToDate}
                onChange={(date: string) => setUpdateFromDate(stringToDate(date, 'ui'))}
              />
              <DayPicker
                label={i18n.t('payroll_bundle.export.period_date_range.to_label')}
                value={dateToString(updateToDate, 'ui')}
                minDate={updateFromDate}
                onChange={(newDate: string) => setUpdateToDate(stringToDate(newDate, 'ui'))}
              />
            </Box>
          )}

          {EVENT_TYPES.has(type) && !hasProfessionals && !canSeePayrollBundleIncidences && (
            <WarningBanner
              description={i18n.t('payroll_bundle.export.activate_events_new_message', {
                type: getTypeLabel(type),
              })}
              action={i18n.t('payroll_bundle.export.activate_events_new_action')}
              onClickAction={() => {
                const path = hasCompensationsModule
                  ? navigation.compensations.inhouseActivation.fullPath()
                  : navigation.payrollBundle.inhouseActivation.fullPath()
                navigate(path)
                const trackingData = {
                  export_format: type,
                  company_id: companyId,
                }
                multiTrack(
                  [
                    trackingEvents.payroll.goToEventLanding,
                    trackingEvents.compensations.goToEmployeeUpdatesEventLanding,
                  ],
                  trackingData
                )
              }}
            />
          )}

          {EVENT_TYPES.has(type) &&
            !hasProfessionals &&
            canSeePayrollBundleIncidences &&
            !isEmployeeEventActivated && (
              <WarningBanner
                description={i18n.t('payroll_bundle.export.activate_events_new_message', {
                  type: getTypeLabel(type),
                })}
                action={i18n.t('payroll_bundle.export.activate_eventes_new_action2')}
                onClickAction={() => {
                  const path = hasCompensationsModule
                    ? navigation.settings.compensations.employeeUpdates.fullPath()
                    : navigation.settings.payroll_bundle.incidences.fullPath()
                  navigate(path)
                  const trackingData = {
                    export_format: type,
                    company_id: companyId,
                  }
                  multiTrack(
                    [
                      trackingEvents.payroll.goToEventSettings,
                      trackingEvents.compensations.goToEmployeeUpdatesEventSettings,
                    ],
                    trackingData
                  )
                }}
              />
            )}

          {description && (
            <Box paddingTop="s16">
              <Callout
                type="info"
                description={description}
                link={
                  type === ExportFormat.SILAE_LEAVES ? (
                    <Link.Touchable onClick={() => navigateToSilaeSettings()}>
                      {i18n.t('payroll_bundle.export.silae_codes_settings_button')}
                    </Link.Touchable>
                  ) : undefined
                }
              />
            </Box>
          )}

          {canExportLastYearData && (
            <CalculationStatusNotice payrollCalculationStartedAt={payrollCalculationStartedAt} />
          )}

          <Box gap="s24">
            {allowDateSelectors && !isValidRange() && (
              // BUG: This is showing incorrectly the first time the modal is opened
              <Box
                background="grey200"
                borderRadius={{
                  all: 'abs008',
                }}
                paddingY="s16"
                paddingX="s24"
              >
                <Text.Normal color="grey1000" size="300">
                  {i18n.t('payroll_bundle.export.error_date_range')}
                </Text.Normal>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    )
  }

  const modalCanBeShown = hasCompensationsModule || periodIndex != null
  const loading =
    loadingCollections || loadingPolicyPeriodCollection || loadingFeatures || loadingPolicies
  return <>{modalCanBeShown && !loading && modal()}</>
}

export default PayrollExportModal
